<template>
  <BaseModule
    class="container column-slider"
    data-module="column-slider"
    v-bind="$props"
    :style="{
      '--slide-bg-color': slideBackgroundColor,
      '--slide-content-size-desk': `${slideTextSizeDesk}px`,
      '--slide-content-size-mobile': `${slideTextSizeMobile}px`,
      '--slide-text-color': slideTextColor,
      '--slide-width': `${slideWidth + 24}px`,
    }"
  >
    <div class="content">
      <div
        v-if="title || text"
        class="header-wrapper"
      >
        <header
          v-if="title || text"
          class="header base-title-fontsize"
        >
          <component
            :is="titleTag"
            v-if="title"
            class="tit tit-g"
            :style="{ fontWeight: titleWeight }"
          >
            {{ title }}
          </component>
          <div
            v-if="text"
            class="txt"
            v-html="text"
          ></div>
        </header>
      </div>

      <!-- Slider main container -->
      <div
        ref="swiper"
        class="swiper"
      >
        <!-- Additional required wrapper -->
        <div
          class="swiper-wrapper"
          :class="{
            disabled: !isSliderEnabled && !isMobile,
          }"
        >
          <div
            v-for="(slide, idx) in slides"
            :key="slide.id || idx"
            class="swiper-slide"
            :class="{ 'custom-width': isSlideWidthFixed && !isMobile }"
            :style="{ maxWidth: maxSlideWidth }"
          >
            <component
              :is="isLinkStretched ? 'EditorLink' : 'div'"
              v-bind="isLinkStretched ? { model: slide.linkUrl } : {}"
            >
              <LImage
                :src="
                  isMobile && slide.differentMobileBackgroundImage
                    ? slide.imageMobile
                    : slide.image
                "
                :alt="
                  isMobile &&
                  slide.differentMobileBackgroundImage &&
                  slide.altTextMobile
                    ? slide.altTextMobile
                    : slide.altText
                "
              />
            </component>
            <article>
              <h3
                v-if="slide.title"
                class="tit"
                :style="{ fontWeight: slide.titleWeight }"
              >
                {{ slide.title }}
              </h3>

              <div
                v-if="slide.text"
                class="txt"
              >
                <span v-html="slide.text"></span>
              </div>
              <EditorLink
                :model="slide.linkUrl"
                class="know-more"
              >
                <span v-if="showButton(slide.buttonText)">
                  {{ slide.buttonText }}
                  <IconArrow
                    size="sm"
                    direction="right"
                  />
                </span>
              </EditorLink>
            </article>
          </div>
        </div>
        <!-- If we need navigation buttons -->
        <div v-show="isSliderEnabled || isMobile">
          <div :class="`swiper-button-prev btn-prev-${instanceId}`"></div>
          <div :class="`swiper-button-next btn-next-${instanceId}`"></div>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapState } from 'vuex';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import debounce from 'lodash/debounce';

import weightOptions from '@/helpers/weightOptions.json';
import showButton from '@/mixins/showButton';
import base from '@/mixins/base';
import EditorLink from '@/modules/EditorLink.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'ColumnSlider',
  components: {
    BaseModule,
    EditorLink,
    IconArrow,
  },
  mixins: [showButton, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    text: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: '',
    },
    slides: {
      type: Array,
      default() {
        return [];
      },
    },
    isLinkStretched: {
      type: Boolean,
      default: true,
    },
    isSliderEnabled: {
      type: Boolean,
      default: true,
    },
    isSlideWidthFixed: {
      type: Boolean,
      default: false,
    },
    slideWidth: {
      type: Number,
      default: 600,
    },
    backgroundColor: {
      type: String,
      default: '#ffffff',
    },
    slideBackgroundColor: {
      type: String,
      default: '#ffffff',
    },
    slideTextColor: {
      type: String,
      default: '#000000',
    },
    buttonTextColor: {
      type: String,
      default: '#3e9aa6',
    },
    slideTextSizeDesk: {
      type: Number,
      default: 18,
    },
    slideTextSizeMobile: {
      type: Number,
      default: 16,
    },
  },
  data() {
    return {
      mobileMaxWidth: 700,
      innerWidth: null,
      swiper: null,
      isMobile: false,
    };
  },
  computed: {
    editing() {
      return typeof this.$parent === 'undefined';
    },
    maxSlideWidth() {
      if (this.editing && !this.isSlideWidthFixed) {
        return '320px';
      }
      return '100%';
    },
    ...mapState(['currentEditableItem', 'activeInstance']),
  },
  watch: {
    isSliderEnabled() {
      this.swiper.slideToLoop(0);
    },
    currentEditableItem() {
      this.slideToActivatedItem();
    },
  },
  mounted() {
    this.swiperInit();
    this.onResize();
    window.addEventListener('resize', debounce(this.onResize, 150));
  },
  methods: {
    slideToActivatedItem() {
      if (
        this.activeInstance === this.instanceId &&
        this.currentEditableItem != null &&
        this.isSliderEnabled
      ) {
        if (this.currentEditableItem.includes('slides')) {
          const itemPathIndex = Number(
            this.currentEditableItem.split('.').pop(),
          );
          if (this.swiper && itemPathIndex < this.swiper.slides.length) {
            this.swiper.slideToLoop(itemPathIndex);
          }
        }
      }
    },
    onResize() {
      const isMobile = window.innerWidth < this.mobileMaxWidth;
      this.innerWidth = window.innerWidth;

      // If "device" changed...
      if (isMobile !== this.isMobile) {
        this.isMobile = isMobile;
      }
    },
    swiperInit() {
      this.$nextTick(() => {
        this.swiper = new Swiper(this.$refs.swiper, {
          modules: [Navigation],
          observer: true,
          spaceBetween: 12,
          slidesPerView: 1.1,
          allowTouchMove: false,
          breakpoints: {
            700: {
              spaceBetween: 12,
              slidesPerView:
                this.isSlideWidthFixed || this.editing ? 'auto' : 2,
            },
            1020: {
              spaceBetween: 20,
              slidesPerView:
                this.isSlideWidthFixed || this.editing ? 'auto' : 3,
            },
            1400: {
              spaceBetween: 20,
              slidesPerView:
                this.isSlideWidthFixed || this.editing ? 'auto' : 4,
            },
          },
          navigation: {
            nextEl: `.btn-next-${this.instanceId}`,
            prevEl: `.btn-prev-${this.instanceId}`,
          },
          on: {
            observerUpdate: () => {
              if (this.isSliderEnabled) {
                this.swiper.slideToLoop(this.slides.length - 1);
              }
            },
          },
        });
      });
    },
    swiperDestroy() {
      this.swiper.destroy();
      this.swiper = null;
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
            ],
            toRemove: ['subtitle'],
          },
          {
            name: 'slides',
            label: 'Slides',
            fields: [
              {
                name: 'slides',
                type: 'List',
                itemLabel: 'Slide {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar slide',
                fields: [
                  {
                    name: 'image',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem',
                    helpText: 'Dimensões recomendadas: 490x480',
                  },
                  {
                    name: 'altText',
                    type: 'ShortText',
                    label: 'Texto alternativo',
                  },
                  {
                    name: 'differentMobileBackgroundImage',
                    type: 'Checkbox',
                    label: 'Usar uma imagem diferente para mobile',
                  },
                  {
                    name: 'imageMobile',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem mobile',
                    helpText: 'Dimensões recomendadas: 490x480',
                    isEnabled() {
                      return this.differentMobileBackgroundImage;
                    },
                  },
                  {
                    name: 'altTextMobile',
                    type: 'ShortText',
                    label: 'Texto alternativo mobile',
                    isEnabled() {
                      return this.differentMobileBackgroundImage;
                    },
                  },
                  {
                    name: 'title',
                    type: 'ShortText',
                    label: 'Título',
                  },
                  {
                    name: 'titleWeight',
                    type: 'VueSelect',
                    label: 'Peso do título',
                    default: '300',
                    options: weightOptions,
                    isEnabled({ title }) {
                      return title;
                    },
                  },
                  {
                    name: 'text',
                    type: 'Html',
                    label: 'Texto de apoio',
                    wrapWithParagraph: false,
                  },
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 15,
                  },
                  {
                    name: 'linkUrl',
                    type: 'Url',
                  },
                ],
              },
            ],
          },
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'isLinkStretched',
                type: 'Checkbox',
                label: 'Link na imagem',
                helpText: 'Estende a área clicável dos links para o item todo.',
              },
              {
                name: 'isSliderEnabled',
                type: 'Checkbox',
                label: 'Ativar deslizamento dos itens',
                helpText:
                  'Ativa o deslizamento dos itens como carrossel. Se desligado, os itens são organizados em mosaico.<br>Obs: O deslizamento sempre estará ativo em modo Mobile.',
              },
              {
                name: 'isSlideWidthFixed',
                type: 'Checkbox',
                label: 'Largura fixa para as imagens',
                helpText:
                  'Fixa a largura das imagens dos slides para a resolução informada.',
              },
              {
                name: 'slideWidth',
                label: 'Resolução de largura das imagens',
                type: 'Range',
                min: 300,
                max: 800,
                step: 1,
                isEnabled({ isSlideWidthFixed }) {
                  return isSlideWidthFixed;
                },
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'slideBackgroundColor',
                type: 'ColorPicker',
                label: 'Cor do fundo dos slides',
              },
              {
                name: 'slideTextColor',
                type: 'ColorPicker',
                label: 'Cor do texto dos slides',
              },
              {
                name: 'buttonTextColor',
                label: 'Cor de texto do link',
                after: 'slideTextColor',
              },
            ],
            toRemove: ['buttonColor'],
          },
          {
            name: 'fonts',
            fields: [
              {
                name: 'slideTextSizeDesk',
                type: 'Range',
                label: 'Slide desktop',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'slideTextSizeMobile',
                type: 'Range',
                label: 'Slide mobile',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.column-slider {
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  overflow: hidden;

  &.container {
    max-width: 100%;
  }

  .content {
    max-width: 100%;
    padding-left: 8vw;
    padding-right: 8vw;

    .header-wrapper {
      display: flex;
      justify-content: center;

      .header {
        text-align: center;
        max-width: 980px;

        .txt {
          line-height: 1.7em;

          &::v-deep {
            strong {
              font-weight: $lb-bold !important;
            }

            a,
            a:visited {
              color: inherit;
              text-decoration: underline;
            }
          }
        }
      }
    }
    .swiper {
      overflow: initial;

      .swiper-wrapper {
        &.disabled {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 20px;

          @include media-breakpoint-down(lg) {
            gap: 12px;
          }

          .swiper-slide {
            margin-right: 0 !important;
          }
        }
        .swiper-slide {
          display: flex;
          flex-direction: column;
          height: auto;
          padding: 12px;
          background: var(--slide-bg-color, $lb-white);
          border-radius: 8px;
          max-width: 100%;

          &.custom-width {
            width: var(--slide-width) !important;
          }

          .img {
            width: 100%;
            height: 450px;
            border-radius: 8px;

            @include media-breakpoint-down(sm) {
              height: 380px;
            }
          }

          article {
            width: 100%;
            padding: 32px 0 0;
            margin: auto;
            text-align: initial;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            font-size: var(--slide-content-size-desk, 18px);

            .tit {
              color: var(--slide-text-color, $lb-black);
              font-weight: $lb-light;
              font-size: 1em;
              line-height: 1.4;
              height: 2.8em;

              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              overflow: hidden;
              -webkit-box-orient: vertical;
            }

            .txt {
              font-size: inherit;
              margin-top: 16px;

              > span {
                color: var(--slide-text-color, $lb-black);
                opacity: 0.7;
                font-size: 0.88em;
                line-height: 1.4;
                height: 4.2em;
                font-weight: $lb-medium;

                display: -webkit-box;
                -webkit-line-clamp: 3;
                overflow: hidden;
                -webkit-box-orient: vertical;
              }
            }

            .know-more {
              margin-top: auto;
              color: var(--button-text-color, $primary-120);

              span {
                display: block;
                text-align: left;
                font-size: 0.88em;
                line-height: 1.4;
                height: 1.4em;
                margin-top: 16px;
                font-weight: $lb-medium;
              }
            }

            @include media-breakpoint-down(sm) {
              font-size: var(--slide-content-size-mobile, 16px);
            }
          }
        }
      }

      .swiper-button-prev,
      .swiper-button-next {
        width: auto;
        @include media-breakpoint-up(lg) {
          margin-left: -8vw;
          margin-right: -8vw;
        }

        &.swiper-button-disabled {
          display: none;
        }

        &::after {
          background: var(--slide-bg-color, $lb-white);
          box-shadow: 0px 10px 25px rgba(0, 0, 0, 0.1);
          color: var(--slide-text-color, $gray-900);
          border-radius: 50px;
          width: 64px;
          height: 64px;
          font-size: 20px;
          display: flex;
          justify-content: center;
          align-items: center;

          @include media-breakpoint-down(lg) {
            width: 48px;
            height: 48px;
            font-size: 18px;
            border-radius: 100px;
          }
        }
      }
    }

    @include media-breakpoint-down(md) {
      padding: 0;
    }
  }

  @include media-breakpoint-down(sm) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }
}
</style>
